import z from "zod";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getRemoteCollectionState, type RemoteCollectionState } from "src/api/remoteCollectionState";
import { zodParseWithLog } from "../../apiErrorLogs";
import { fetchAllHydraMembers } from "./fetchAllHydraMembers";
import { assertTransportDetailScope, isTransportDetailNotFoundError } from "./transportDetailScope";
import { TransportMutationContractError } from "./TransportMutationContractError";
import { TransportLegSchema, type TransportLinkMode } from "./useTransportLegs";

const TransportCircuitLegSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    orderIndex: z.number(),
    transportLeg: z.union([z.string(), TransportLegSchema]),
});

const TransportLinkModeApiSchema = z
    .enum(["LOCAL", "LINKED", "local", "linked"])
    .transform((value) => value.toUpperCase() as TransportLinkMode);

export const TransportCircuitSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    organization: z.union([z.string(), z.object({ "@id": z.string() })]).optional(),
    linkMode: TransportLinkModeApiSchema,
    sourceReferenceCircuitId: z.number().nullable().optional(),
    name: z.string().nullable().optional(),
    originCountryCode: z.string().nullable().optional(),
    destinationCountryCode: z.string().nullable().optional(),
    circuitLegs: z.array(TransportCircuitLegSchema).default([]),
});

export type TransportCircuit = z.infer<typeof TransportCircuitSchema>;
export type TransportCircuitsState = RemoteCollectionState<TransportCircuit>;

export type TransportCircuitLegWrite = {
    "@id"?: string;
    transportLeg: IRI;
    orderIndex: number;
};

export type TransportCircuitWrite = {
    "@id"?: string;
    organization: IRI;
    linkMode: TransportLinkMode;
    sourceReferenceCircuitId?: number | null;
    name?: string | null;
    originCountryCode?: string | null;
    destinationCountryCode?: string | null;
    circuitLegs?: TransportCircuitLegWrite[];
};

export const transportCircuitsKey = "transportCircuits";

export const transportCircuitKeys = {
    list: (organizationId?: number) => [transportCircuitsKey, "list", organizationId ?? null] as const,
    detail: (organizationId?: number, id?: number) =>
        [transportCircuitsKey, "detail", organizationId ?? null, id ?? null] as const,
};

export const useTransportCircuits = (params?: { organizationId?: number; enabled?: boolean }) => {
    const organizationId = params?.organizationId;
    const enabled = (params?.enabled ?? true) && organizationId !== undefined;

    const query = useQuery({
        enabled,
        keepPreviousData: false,
        queryKey: transportCircuitKeys.list(organizationId),
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load transport circuits while disabled or without an organization");
            }

            const members = await fetchAllHydraMembers("/api/transport-circuits", {
                organization: organizationId,
            });
            return zodParseWithLog(members, TransportCircuitSchema.array(), "useTransportCircuits");
        },
    });
    const transportCircuitsState: TransportCircuitsState = getRemoteCollectionState({
        data: query.data,
        enabled,
        error: query.error,
        isLoading: query.isLoading,
    });

    return {
        transportCircuits: enabled ? (query.data ?? []) : undefined,
        transportCircuitsState,
        ...query,
        data: enabled ? query.data : undefined,
    };
};

export const useTransportCircuit = (id?: number, options?: { enabled?: boolean; organizationId?: number }) => {
    const organizationId = options?.organizationId;
    const enabled = (options?.enabled ?? true) && id !== undefined && organizationId !== undefined;
    const query = useQuery({
        enabled,
        keepPreviousData: false,
        queryKey: transportCircuitKeys.detail(organizationId, id),
        queryFn: async () => {
            if (id === undefined || organizationId === undefined) {
                throw new Error("A transport circuit id and organization id are required");
            }

            const request = await loggedInApiCall.get(`/api/transport-circuits/${id}`);
            const transportCircuit = zodParseWithLog(request.data, TransportCircuitSchema, "useTransportCircuit");
            return assertTransportDetailScope(transportCircuit, id, organizationId, "Transport circuit");
        },
    });

    return {
        isNotFound: query.isError && isTransportDetailNotFoundError(query.error),
        transportCircuit: query.data,
        ...query,
    };
};

export const useUpsertTransportCircuit = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (circuit: TransportCircuitWrite & { id?: number }) => {
            const payload = toTransportCircuitApiPayload(circuit);
            const response =
                circuit.id !== undefined ?
                    await loggedInApiCall.put("/api/transport-circuits/" + circuit.id, payload)
                :   await loggedInApiCall.post("/api/transport-circuits", payload);

            try {
                return zodParseWithLog(response.data, TransportCircuitSchema, "useUpsertTransportCircuit");
            } catch (error) {
                throw new TransportMutationContractError(error);
            } finally {
                void queryClient.invalidateQueries([transportCircuitsKey]);
            }
        },
    });
};

const toTransportCircuitApiPayload = (circuit: TransportCircuitWrite & { id?: number }) => ({
    ...circuit,
    linkMode: circuit.linkMode.toLowerCase(),
});

export const useDeleteTransportCircuit = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete("/api/transport-circuits/" + id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([transportCircuitsKey]);
        },
    });
};
